import { useEffect, useRef, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { authQueryOptions } from "@/auth/queryOptions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormGroupDefault,
  FormGroupSwitch,
  PageContent,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";
import { CompaniesSection } from "@/components/settings/companies-section";
import { DeployedVersions } from "@/components/settings/deployed-versions";

interface SettingsResp {
  values: Record<string, unknown>;
  definitions: { key: string; description: string }[];
}

interface SystemForm {
  retention_days: number;
  retention_sweep_seconds: number;
  max_log_bytes_per_step: number;
  include_deleted_default: boolean;
}

/**
 * Settings page. Two sections:
 *   1. System — global ETL knobs (retention, log caps, default args).
 *   2. Companies — list/add/edit BeProduct tenants. Per-company secrets
 *      and the "Get refresh token via BeProduct" OAuth flow live here.
 *
 * Search params: `?oauth=ok|fail&tenant=<slug>&reason=<msg>` — the
 * tenant-OAuth callback redirects back here so we can surface the
 * outcome as a toast and reopen the relevant row.
 */
const searchSchema = z.object({
  oauth: z.enum(["ok", "fail"]).optional(),
  tenant: z.string().optional(),
  reason: z.string().optional(),
  company: z.string().optional(),
});

export const Route = createFileRoute("/_authed/settings")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    // Settings is global admin chrome: tenant connections, system
    // tunables. Non-admins (members with no IDS admin grant anywhere)
    // bounce back to /, which re-resolves them to /<slug>/reports.
    const me = await context.queryClient.ensureQueryData(authQueryOptions);
    const adminDomains = me?.adminDomains ?? [];
    if (adminDomains.length === 0) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsPage,
});

function SettingsPage() {
  const search = Route.useSearch();
  const nav = useNavigate({ from: "/settings" });
  const qc = useQueryClient();
  const toastedRef = useRef(false);

  // Surface the OAuth callback result as a toast, then strip the
  // params so the same toast doesn't fire again on re-render. Ref
  // guards StrictMode's double-mount in dev.
  useEffect(() => {
    if (toastedRef.current) return;
    if (search.oauth === "ok") {
      toastedRef.current = true;
      toast.success(
        search.tenant
          ? `Refresh token captured for ${search.tenant}`
          : "Refresh token captured",
      );
      qc.invalidateQueries({ queryKey: ["tenant-secrets"] });
      nav({ search: { company: search.company } });
    } else if (search.oauth === "fail") {
      toastedRef.current = true;
      toast.error(`OAuth failed: ${search.reason ?? "unknown"}`);
      nav({ search: { company: search.company } });
    }
  }, [search.oauth, search.reason, search.tenant, search.company, nav, qc]);

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6 max-w-4xl">
        <PageHeader title="Settings" />

        <SystemSection />

        <DeployedVersions />

        <CompaniesSection expandTenantId={search.company ?? null} />
      </div>
    </PageContent>
  );
}

function SystemSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<SettingsResp>("/api/settings"),
  });

  const [form, setForm] = useState<SystemForm | null>(null);

  useEffect(() => {
    if (q.data) {
      setForm({
        retention_days: Number(q.data.values.retention_days ?? 30),
        retention_sweep_seconds: Number(q.data.values.retention_sweep_seconds ?? 3600),
        max_log_bytes_per_step: Number(q.data.values.max_log_bytes_per_step ?? 2_000_000),
        include_deleted_default: Boolean(q.data.values.include_deleted_default ?? true),
      });
    }
  }, [q.data]);

  const m = useMutation({
    mutationFn: (data: SystemForm) =>
      api("/api/settings", { method: "PATCH", body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success("Saved. Worker re-reads on each sweep — no restart needed.");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (q.error) {
    return (
      <Card>
        <CardContent className="p-6 text-destructive">
          Failed to load system settings.
        </CardContent>
      </Card>
    );
  }
  if (q.isLoading || !q.data || !form) {
    return (
      <Card>
        <CardContent className="p-6 text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  const description = (key: string) =>
    q.data.definitions.find((d) => d.key === key)?.description ?? "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>System</CardTitle>
        <CardDescription>
          Global ETL knobs. Worker re-reads on each sweep — no restart needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="font-medium text-sm mb-2">Run retention</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormGroupDefault
              label="Retention (days)"
              type="number"
              min={0}
              value={form.retention_days}
              onChange={(e) => setForm({ ...form, retention_days: Number(e.target.value) })}
            />
            <FormGroupDefault
              label="Sweep cadence (sec)"
              type="number"
              min={60}
              value={form.retention_sweep_seconds}
              onChange={(e) =>
                setForm({ ...form, retention_sweep_seconds: Number(e.target.value) })
              }
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">{description("retention_days")}</p>
        </div>

        <div>
          <h3 className="font-medium text-sm mb-2">Step logs</h3>
          <FormGroupDefault
            label="Max log bytes per step"
            type="number"
            min={1024}
            value={form.max_log_bytes_per_step}
            onChange={(e) =>
              setForm({ ...form, max_log_bytes_per_step: Number(e.target.value) })
            }
          />
          <p className="text-xs text-muted-foreground mt-2">
            {description("max_log_bytes_per_step")}
          </p>
        </div>

        <div>
          <h3 className="font-medium text-sm mb-2">Tenant defaults</h3>
          <FormGroupSwitch
            label="Include deleted entities"
            checked={form.include_deleted_default}
            onCheckedChange={(checked) =>
              setForm({ ...form, include_deleted_default: checked })
            }
          />
          <p className="text-xs text-muted-foreground mt-2">
            {description("include_deleted_default")}
          </p>
        </div>

        <div>
          <Button onClick={() => m.mutate(form)} disabled={m.isPending}>
            {m.isPending ? "Saving…" : "Save system settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
